import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Tabs,
  Tab,
  Grid,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListSubheader,
  CircularProgress,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import { Add, Delete, Edit, DragIndicator } from '@mui/icons-material';
import { TenantPickerList } from '@/components/platform/TenantPickerList';
import { PLAN_ICON_OPTIONS, resolvePlanIcon } from '@/constants/planIconOptions';
import { apiService } from '@/services/api';
import {
  formatCurrency,
  parseCurrency,
  formatPercentage,
  parsePercentage,
  numberToEditableString
} from '@/utils/formatMask';
import AdvancedFilters from '@/components/AdvancedFilters';
import { ClientCell } from '@/components/ClientCell';
import { ProductCell } from '@/components/ProductCell';
import { useFilters } from '@/contexts/FilterContext';
import { useMessage } from '@/contexts/MessageContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useProductsMap } from '@/hooks/useProductsMap';
import { logger } from '@/utils/logger';
import { ProductScopeGate } from '@/components/ProductScopeGate';
import { Suspense } from 'react';
import { PRICING_SCHEMA_REGISTRY } from '@/components/platform/pricingSchemas/registry';

export const PricingPage: React.FC = () => {
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const { showSuccess, showError } = useMessage();
  const { setRefreshCallback } = useRefresh();
  // Valores brutos durante edição: permitem decimais (0,20) e não parseiam a cada tecla
  const [rawInputValues, setRawInputValues] = useState<Record<string, string>>({});

  // Filtros globais de clientes
  const { selectedClients, setSelectedClients, productId } = useFilters();
  const productsById = useProductsMap();
  const pricingSchema = productsById.get(productId)?.integrations?.pricingSchema;
  const schemaEntry = pricingSchema ? PRICING_SCHEMA_REGISTRY[pricingSchema] : null;
  const schemaEntryRef = useRef(schemaEntry);
  schemaEntryRef.current = schemaEntry;

  // Tenants carregados (para nomes e criação de configs por cliente)
  const [tenants, setTenants] = useState<any[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);

  // Estado para criação/edição de configuração por cliente
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [tenantFormDialogOpen, setTenantFormDialogOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [tenantForm, setTenantForm] = useState<any | null>(null);
  const [tenantSaving, setTenantSaving] = useState(false);

  // Valores brutos específicos para o formulário do tenant (no modal)
  const [tenantRawInputValues, setTenantRawInputValues] = useState<Record<string, string>>({});
  const [tenantRawNumericValues, setTenantRawNumericValues] = useState<Record<string, string>>({});

  // Uso real do tenant e erros de validação (Por cliente)
  const [tenantUsage, setTenantUsage] = useState<{
    userCount: number;
    clientCount: number;
    nucleusCount: number;
  } | null>(null);
  const [tenantValidationErrors, setTenantValidationErrors] = useState<string[]>([]);
  const [configMissing, setConfigMissing] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [dragPackIndex, setDragPackIndex] = useState<number | null>(null);
  const [pendingPlanFocusIndex, setPendingPlanFocusIndex] = useState<number | null>(null);
  const planLabelInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const loadConfig = useCallback(async () => {
    if (!productId?.trim()) return;
    if (schemaEntryRef.current) return;
    setLoading(true);
    setConfigMissing(false);
    setLoadError(null);
    try {
      const response = await apiService.getPricingConfig();
      if (response.data.success) {
        const data = response.data.data;
        const packages = data.packages ?? {};
        const apGest = data.availablePackages?.find(
          (ap: { key?: string }) => (ap.key || '').toLowerCase() === 'gestao'
        );
        const gestaoPacks =
          apGest?.condoCapacityPacks ??
          apGest?.clientCapacityPacks ??
          packages.gestao?.condoCapacityPacks ??
          [];
        const gestaoOnlyAps = (data.availablePackages ?? []).filter(
          (ap: { key?: string }) => (ap.key || '').toLowerCase() === 'gestao'
        );

        setConfig({
          ...data,
          availablePackages: gestaoOnlyAps,
          packages: {
            gestao: {
              ...(packages.gestao ?? {}),
              condoCapacityPacks: Array.isArray(gestaoPacks) ? gestaoPacks : [],
              unifiedBillingForCondos: true
            }
          }
        });
      }
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setConfig(null);
        setConfigMissing(true);
      } else {
        setLoadError(error);
        logger.error('Error loading pricing config:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [productId]);

  const loadTenants = useCallback(async () => {
    setTenantsLoading(true);
    try {
      const filters: any = {
        page: 1,
        limit: 1000
      };

      if (selectedClients.length > 0) {
        filters.tenantIds = selectedClients;
      }

      const response = await apiService.getTenants({ ...filters, productId });
      if (response.data.success) {
        setTenants(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error loading tenants for pricing:', error);
    } finally {
      setTenantsLoading(false);
    }
  }, [selectedClients, productId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig, productId]);

  useEffect(() => {
    if (pendingPlanFocusIndex == null) return;
    const el = planLabelInputRefs.current[pendingPlanFocusIndex];
    if (el) {
      el.focus();
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    setPendingPlanFocusIndex(null);
  }, [config?.packages?.gestao?.condoCapacityPacks?.length, pendingPlanFocusIndex]);

  useEffect(() => {
    if (tabValue === 1) {
      loadTenants();
    }
  }, [tabValue, loadTenants]);

  const updateGestao = useCallback((partial: Record<string, unknown>) => {
    setConfig((prev: any) => ({
      ...prev,
      packages: {
        ...prev.packages,
        gestao: { ...prev.packages?.gestao, ...partial }
      }
    }));
  }, []);

  const updateGestaoEnabled = useCallback((enabled: boolean) => {
    setConfig((prev: any) => {
      const aps = Array.isArray(prev.availablePackages) ? [...prev.availablePackages] : [];
      const idx = aps.findIndex((ap: any) => (ap.key || '').toLowerCase() === 'gestao');
      if (idx >= 0) {
        aps[idx] = { ...aps[idx], enabled };
      } else {
        aps.push({ key: 'gestao', enabled });
      }
      return { ...prev, availablePackages: aps };
    });
  }, []);

  // Registrar callback de refresh para atualizar apenas o conteúdo da página
  useEffect(() => {
    const doRefresh = async () => {
      await loadConfig();
      await loadTenants();
    };
    setRefreshCallback(doRefresh);
    return () => setRefreshCallback(null);
  }, [loadConfig, loadTenants, setRefreshCallback]);

  const handleSave = async () => {
    setSaving(true);

    try {
      const gestaoPacks = config?.packages?.gestao?.condoCapacityPacks ?? [];
      const apGest = (config?.availablePackages ?? []).find(
        (ap: { key?: string }) => (ap.key || '').toLowerCase() === 'gestao'
      );

      const payload: Record<string, unknown> = {
        isActive: config?.isActive ?? true,
        availablePackages: [
          {
            key: 'gestao',
            enabled: apGest?.enabled !== false,
            condoCapacityPacks: Array.isArray(gestaoPacks)
              ? gestaoPacks.map((p: Record<string, unknown>, i: number) => ({
                  ...p,
                  order: i
                }))
              : [],
            order: 1
          }
        ]
      };

      const response = await apiService.updatePricingConfig(payload);
      if (response.data.success) {
        showSuccess('Configuração salva com sucesso!');
        await loadConfig();
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao salvar configuração');
      logger.error('Error saving pricing config:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handlers para formulário de tenant (no modal)
  const handleTenantAddPriceTier = () => {
    const newTier = { minUnits: 0, maxUnits: null, pricePerUnit: 0 };
    setTenantForm((prev: any) => ({
      ...prev,
      priceTiers: [...(prev.priceTiers || []), newTier]
    }));
  };

  const handleTenantUpdatePriceTier = (index: number, field: string, value: any) => {
    const updatedTiers = [...(tenantForm.priceTiers || [])];
    updatedTiers[index] = { ...updatedTiers[index], [field]: value };
    setTenantForm((prev: any) => ({ ...prev, priceTiers: updatedTiers }));
  };

  const handleTenantDeletePriceTier = (index: number) => {
    const updatedTiers = (tenantForm.priceTiers || []).filter((_: any, i: number) => i !== index);
    setTenantForm((prev: any) => ({ ...prev, priceTiers: updatedTiers }));
  };

  // Handlers (legado) ainda usados em partes do modal. Serão removidos na limpeza final.
  const handleTenantAddVolumeDiscount = () => {
    const newDiscount = { minTotalUnits: 0, discountPercentage: 0 };
    setTenantForm((prev: any) => ({
      ...prev,
      volumeDiscounts: [...(prev.volumeDiscounts || []), newDiscount]
    }));
  };

  const handleTenantUpdateVolumeDiscount = (index: number, field: string, value: any) => {
    const updatedDiscounts = [...(tenantForm.volumeDiscounts || [])];
    updatedDiscounts[index] = { ...updatedDiscounts[index], [field]: value };
    setTenantForm((prev: any) => ({ ...prev, volumeDiscounts: updatedDiscounts }));
  };

  const handleTenantDeleteVolumeDiscount = (index: number) => {
    const updatedDiscounts = (tenantForm.volumeDiscounts || []).filter(
      (_: any, i: number) => i !== index
    );
    setTenantForm((prev: any) => ({ ...prev, volumeDiscounts: updatedDiscounts }));
  };

  const handleTenantAddExtraUserPackage = () => {
    const newPackage = { quantity: 1, price: 0 };
    const currentExtraUserPricing = tenantForm.extraUserPricing || {
      individualPrice: 0,
      packages: []
    };
    setTenantForm((prev: any) => ({
      ...prev,
      extraUserPricing: {
        ...currentExtraUserPricing,
        packages: [...(currentExtraUserPricing.packages || []), newPackage]
      }
    }));
  };

  const handleTenantUpdateExtraUserPackage = (index: number, field: string, value: any) => {
    const currentExtraUserPricing = tenantForm.extraUserPricing || {
      individualPrice: 0,
      packages: []
    };
    const updatedPackages = [...(currentExtraUserPricing.packages || [])];
    updatedPackages[index] = { ...updatedPackages[index], [field]: value };
    setTenantForm((prev: any) => ({
      ...prev,
      extraUserPricing: {
        ...currentExtraUserPricing,
        packages: updatedPackages
      }
    }));
  };

  const handleTenantDeleteExtraUserPackage = (index: number) => {
    const currentExtraUserPricing = tenantForm.extraUserPricing || {
      individualPrice: 0,
      packages: []
    };
    const updatedPackages = (currentExtraUserPricing.packages || []).filter(
      (_: any, i: number) => i !== index
    );
    setTenantForm((prev: any) => ({
      ...prev,
      extraUserPricing: {
        ...currentExtraUserPricing,
        packages: updatedPackages
      }
    }));
  };

  const handleTenantAddProgressiveUnitDiscount = () => {
    setTenantForm((prev: any) => ({
      ...prev,
      progressiveUnitDiscounts: [
        ...(prev.progressiveUnitDiscounts || []),
        { minTotalUnits: 0, maxTotalUnits: null, discountPercentage: 0 }
      ]
    }));
  };

  const handleTenantUpdateProgressiveUnitDiscount = (index: number, field: string, value: any) => {
    const cur = [...(tenantForm.progressiveUnitDiscounts || [])];
    cur[index] = { ...cur[index], [field]: value };
    setTenantForm((prev: any) => ({ ...prev, progressiveUnitDiscounts: cur }));
  };

  const handleTenantDeleteProgressiveUnitDiscount = (index: number) => {
    setTenantForm((prev: any) => ({
      ...prev,
      progressiveUnitDiscounts: (prev.progressiveUnitDiscounts || []).filter(
        (_: any, i: number) => i !== index
      )
    }));
  };

  /** Retorna os valores base do formulário para um tipo de pacote, a partir de config.packages e availablePackages */
  const getBaseFormValuesForKey = useCallback(
    (key: string): any => {
      const pkg = config?.packages?.[key];
      if (!pkg) return null;
      const base: any = {
        packageType: key,
        pricingModel: pkg.pricingModel ?? 'tiered',
        minPricePerClient: key === 'condominio' ? pkg.minPricePerClient : undefined,
        pricePerUnit: key === 'condominio' ? undefined : pkg.pricePerUnit,
        priceTiers: Array.isArray(pkg.priceTiers) ? [...pkg.priceTiers] : [],
        volumeDiscounts: Array.isArray(pkg.volumeDiscounts) ? [...pkg.volumeDiscounts] : [],
        includedUsers: key === 'gestao' || key === 'institucional' ? 0 : (pkg.includedUsers ?? 3),
        extraUserPricing: pkg.extraUserPricing
          ? {
              individualPrice: pkg.extraUserPricing.individualPrice ?? 15,
              packages: Array.isArray(pkg.extraUserPricing.packages)
                ? pkg.extraUserPricing.packages.map((p: any) => ({ ...p }))
                : []
            }
          : { individualPrice: 15, packages: [] },
        condoCapacityPacks: Array.isArray(pkg.condoCapacityPacks)
          ? pkg.condoCapacityPacks.map((c: any) => ({ ...c }))
          : [],
        contractedClientCapacity: undefined,
        contractedExtraUserPackageQuantity: undefined,
        contractedPriceTierMinUnits: undefined,
        nucleusCapacityPacks: Array.isArray(pkg.nucleusCapacityPacks)
          ? pkg.nucleusCapacityPacks.map((n: any) => ({ ...n }))
          : [],
        contractedNucleusCapacity: undefined,
        billingCycle: config?.billingCycle ?? 'monthly',
        billingDueDay: undefined,
        ...(key === 'gestao'
          ? {
              progressiveUnitDiscounts: Array.isArray((pkg as any).progressiveUnitDiscounts)
                ? (pkg as any).progressiveUnitDiscounts.map((t: any) => ({ ...t }))
                : []
            }
          : {})
      };
      return base;
    },
    [config]
  );

  /** Pacotes disponíveis para o Select (apenas aqueles com base em config.packages), ordenados */
  const tenantFormPackageOptions = (config?.availablePackages ?? [])
    .filter((ap: any) => config?.packages?.[ap.key] != null)
    .sort((a: any, b: any) => (a.order ?? 999) - (b.order ?? 999));

  /** Valida se as quantidades escolhidas no formulário excedem o uso real do tenant */
  const validateTenantFormAgainstUsage = useCallback(
    (
      form: any,
      usage: { userCount: number; clientCount: number; nucleusCount: number } | null
    ): string[] => {
      if (!form || !usage) return [];
      const errors: string[] = [];
      const pkgType = form.packageType ?? 'condominio';

      // Condomínio: limite por usuários incluídos + pacote de extras (Gestão/Institucional = ilimitado)
      if (pkgType === 'condominio') {
        const includedUsers = form.includedUsers ?? 0;
        const contractedExtra = form.contractedExtraUserPackageQuantity;
        if (contractedExtra != null && contractedExtra !== '' && Number(contractedExtra) > 0) {
          const extraQty = Number(contractedExtra);
          const maxAllowedUsers = includedUsers + extraQty;
          if (usage.userCount > maxAllowedUsers) {
            errors.push(
              `Usuários: o cliente possui ${usage.userCount} usuário(s) ativo(s), mas o limite escolhido é ${maxAllowedUsers} (${includedUsers} incluídos + ${extraQty} extras). Ajuste o pacote de usuários extras ou os usuários incluídos.`
            );
          }
        }
      }

      // Gestão: capacidade de clientes (condomínios)
      if (pkgType === 'gestao' || pkgType === 'operador') {
        const contractedCap = form.contractedClientCapacity;
        if (
          contractedCap != null &&
          contractedCap !== '' &&
          Number(contractedCap) >= 0 &&
          usage.clientCount > Number(contractedCap)
        ) {
          errors.push(
            `Clientes/condomínios: o cliente possui ${usage.clientCount} cliente(s) cadastrado(s), mas a capacidade contratada escolhida é ${contractedCap}. Ajuste a capacidade de condomínios.`
          );
        }
      }

      // Institucional: capacidade de núcleos
      if (pkgType === 'institucional') {
        const contractedNucleus = form.contractedNucleusCapacity;
        if (
          contractedNucleus != null &&
          contractedNucleus !== '' &&
          Number(contractedNucleus) >= 0 &&
          usage.nucleusCount > Number(contractedNucleus)
        ) {
          errors.push(
            `Núcleos/áreas: o cliente possui ${usage.nucleusCount} núcleo(s) cadastrado(s), mas a capacidade contratada escolhida é ${contractedNucleus}. Ajuste a capacidade de núcleos.`
          );
        }
      }

      return errors;
    },
    []
  );

  /** Aplica valores padrão (primeira opção) aos campos "contracted" quando estão undefined e a lista tem itens, para que o valor exibido seja salvo */
  const applyContractedDefaults = (form: any): any => {
    const next = { ...form };
    if (
      (next.contractedClientCapacity === undefined || next.contractedClientCapacity === null) &&
      (next.condoCapacityPacks?.length ?? 0) > 0
    ) {
      next.contractedClientCapacity = next.condoCapacityPacks?.[0]?.capacity;
    }
    if (
      (next.contractedNucleusCapacity === undefined || next.contractedNucleusCapacity === null) &&
      (next.nucleusCapacityPacks?.length ?? 0) > 0
    ) {
      next.contractedNucleusCapacity = next.nucleusCapacityPacks?.[0]?.capacity;
    }
    /* contractedExtraUserPackageQuantity: não aplicar default (mantém null = Individual); ao mudar de plano deve vir preenchido com null */
    if (
      (next.contractedPriceTierMinUnits === undefined ||
        next.contractedPriceTierMinUnits === null) &&
      (next.priceTiers?.length ?? 0) > 0
    ) {
      next.contractedPriceTierMinUnits = next.priceTiers?.[0]?.minUnits;
    }
    return next;
  };

  const handleOpenTenantForm = (tenantId: string, existingConfig?: any) => {
    setSelectedTenantId(tenantId);
    setTenantRawInputValues({});
    setTenantRawNumericValues({});
    setTenantUsage(null);
    setTenantValidationErrors([]);

    apiService
      .getTenantStats(tenantId)
      .then((res: any) => {
        if (res?.data?.success && res?.data?.data) {
          setTenantUsage({
            userCount: res.data.data.userCount ?? 0,
            clientCount: res.data.data.clientCount ?? 0,
            nucleusCount: res.data.data.nucleusCount ?? 0
          });
        }
      })
      .catch(() => setTenantUsage({ userCount: 0, clientCount: 0, nucleusCount: 0 }));

    const rawPkgType = existingConfig?.packageType ?? 'condominio';
    const pkgType = rawPkgType === 'operador' ? 'gestao' : rawPkgType;
    const effectiveKey = config?.packages?.[pkgType]
      ? pkgType
      : (tenantFormPackageOptions[0]?.key ?? 'condominio');

    if (existingConfig) {
      const base = getBaseFormValuesForKey(effectiveKey);
      if (base) {
        const merged = {
          ...base,
          packageType: effectiveKey,
          pricingModel: existingConfig.pricingModel ?? base.pricingModel,
          minPricePerClient: existingConfig.minPricePerClient ?? base.minPricePerClient,
          pricePerUnit: existingConfig.pricePerUnit ?? base.pricePerUnit,
          priceTiers:
            existingConfig.priceTiers?.length > 0 ? existingConfig.priceTiers : base.priceTiers,
          volumeDiscounts:
            existingConfig.volumeDiscounts?.length > 0
              ? existingConfig.volumeDiscounts
              : base.volumeDiscounts,
          includedUsers:
            effectiveKey === 'gestao' || effectiveKey === 'institucional'
              ? 0
              : (existingConfig.includedUsers ?? base.includedUsers),
          extraUserPricing: existingConfig.extraUserPricing ?? base.extraUserPricing,
          condoCapacityPacks:
            existingConfig.condoCapacityPacks?.length > 0
              ? existingConfig.condoCapacityPacks
              : base.condoCapacityPacks,
          contractedClientCapacity: existingConfig.contractedClientCapacity,
          contractedExtraUserPackageQuantity: existingConfig.contractedExtraUserPackageQuantity,
          contractedPriceTierMinUnits: existingConfig.contractedPriceTierMinUnits,
          progressiveUnitDiscounts: Array.isArray(existingConfig.progressiveUnitDiscounts)
            ? existingConfig.progressiveUnitDiscounts.map((t: any) => ({ ...t }))
            : (base.progressiveUnitDiscounts ?? []),
          nucleusCapacityPacks:
            existingConfig.nucleusCapacityPacks?.length > 0
              ? existingConfig.nucleusCapacityPacks
              : base.nucleusCapacityPacks,
          contractedNucleusCapacity: existingConfig.contractedNucleusCapacity,
          billingCycle: existingConfig.billingCycle ?? base.billingCycle,
          billingDueDay: existingConfig.billingDueDay
        };
        setTenantForm(applyContractedDefaults(merged));
      } else {
        const merged = {
          packageType: effectiveKey,
          pricingModel: existingConfig.pricingModel,
          minPricePerClient: existingConfig.minPricePerClient,
          pricePerUnit: existingConfig.pricePerUnit,
          priceTiers: existingConfig.priceTiers || [],
          volumeDiscounts: existingConfig.volumeDiscounts || [],
          includedUsers:
            effectiveKey === 'gestao' || effectiveKey === 'institucional'
              ? 0
              : existingConfig.includedUsers,
          extraUserPricing: existingConfig.extraUserPricing ?? {
            individualPrice: 15,
            packages: []
          },
          condoCapacityPacks: existingConfig.condoCapacityPacks ?? [],
          contractedClientCapacity: existingConfig.contractedClientCapacity,
          contractedExtraUserPackageQuantity: existingConfig.contractedExtraUserPackageQuantity,
          progressiveUnitDiscounts: Array.isArray(existingConfig.progressiveUnitDiscounts)
            ? existingConfig.progressiveUnitDiscounts
            : [],
          nucleusCapacityPacks: existingConfig.nucleusCapacityPacks ?? [],
          contractedNucleusCapacity: existingConfig.contractedNucleusCapacity,
          billingCycle: existingConfig.billingCycle ?? config?.billingCycle ?? 'monthly',
          billingDueDay: existingConfig.billingDueDay
        };
        setTenantForm(applyContractedDefaults(merged));
      }
    } else {
      const base = getBaseFormValuesForKey(effectiveKey);
      const form = base ?? {
        packageType: effectiveKey,
        pricingModel: 'tiered',
        minPricePerClient: undefined,
        pricePerUnit: undefined,
        priceTiers: [],
        volumeDiscounts: [],
        includedUsers: effectiveKey === 'condominio' ? 3 : 0,
        extraUserPricing: { individualPrice: 15, packages: [] },
        condoCapacityPacks: [],
        contractedClientCapacity: undefined,
        contractedExtraUserPackageQuantity: undefined,
        nucleusCapacityPacks: [],
        contractedNucleusCapacity: undefined,
        billingCycle: config?.billingCycle ?? 'monthly',
        billingDueDay: undefined,
        ...(effectiveKey === 'gestao' ? { progressiveUnitDiscounts: [] } : {})
      };
      setTenantForm(applyContractedDefaults(form));
    }
    setTenantFormDialogOpen(true);
  };

  const handleTenantPackageTypeChange = (key: string) => {
    setTenantRawInputValues({});
    setTenantRawNumericValues({});
    const base = getBaseFormValuesForKey(key);
    setTenantForm((prev: any) => {
      const preservedBillingDueDay =
        prev?.billingDueDay !== undefined && prev?.billingDueDay !== null
          ? prev.billingDueDay
          : undefined;
      const preservedContractedExtraUserPackageQuantity =
        prev?.contractedExtraUserPackageQuantity !== undefined &&
        prev?.contractedExtraUserPackageQuantity !== null
          ? prev.contractedExtraUserPackageQuantity
          : null;
      const unlimitedUsers = key === 'gestao' || key === 'operador' || key === 'institucional';
      const merged = base
        ? {
            ...base,
            packageType: key,
            billingDueDay: preservedBillingDueDay ?? base.billingDueDay,
            contractedExtraUserPackageQuantity: unlimitedUsers
              ? null
              : preservedContractedExtraUserPackageQuantity !== null
                ? preservedContractedExtraUserPackageQuantity
                : (base.contractedExtraUserPackageQuantity ?? null)
          }
        : {
            ...prev,
            packageType: key,
            billingDueDay: preservedBillingDueDay ?? prev?.billingDueDay,
            contractedExtraUserPackageQuantity: unlimitedUsers
              ? null
              : (preservedContractedExtraUserPackageQuantity ??
                prev?.contractedExtraUserPackageQuantity)
          };
      return applyContractedDefaults(merged);
    });
  };

  const handleCloseTenantForm = () => {
    setTenantFormDialogOpen(false);
    setSelectedTenantId(null);
    setTenantForm(null);
    setTenantRawInputValues({});
    setTenantRawNumericValues({});
    setTenantUsage(null);
    setTenantValidationErrors([]);
  };

  useEffect(() => {
    if (tenantForm && tenantFormDialogOpen) {
      const errs = validateTenantFormAgainstUsage(tenantForm, tenantUsage);
      setTenantValidationErrors(errs);
    } else {
      setTenantValidationErrors([]);
    }
  }, [tenantForm, tenantUsage, tenantFormDialogOpen, validateTenantFormAgainstUsage]);

  const handleSaveTenantConfig = async () => {
    if (!selectedTenantId || !tenantForm) return;
    if (tenantValidationErrors.length > 0) {
      showError(
        'Não é possível salvar: as quantidades escolhidas são menores que o uso atual do cliente. Ajuste os valores ou verifique os dados cadastrados.'
      );
      return;
    }
    setTenantSaving(true);
    try {
      const payload: any = {
        packageType: tenantForm.packageType ?? 'condominio',
        pricingModel: tenantForm.pricingModel,
        minPricePerClient:
          tenantForm.packageType === 'condominio' ? tenantForm.minPricePerClient : undefined,
        priceTiers: tenantForm.priceTiers || [],
        volumeDiscounts: tenantForm.volumeDiscounts || [],
        includedUsers:
          tenantForm.packageType === 'gestao' ||
          tenantForm.packageType === 'operador' ||
          tenantForm.packageType === 'institucional'
            ? 0
            : tenantForm.includedUsers,
        extraUserPricing: tenantForm.extraUserPricing,
        billingCycle: tenantForm.billingCycle || config.billingCycle
      };
      if (tenantForm.packageType === 'condominio') {
        payload.contractedPriceTierMinUnits = tenantForm.contractedPriceTierMinUnits;
      }
      if (tenantForm.packageType === 'gestao' || tenantForm.packageType === 'operador') {
        payload.condoCapacityPacks = tenantForm.condoCapacityPacks ?? [];
        payload.contractedClientCapacity = tenantForm.contractedClientCapacity;
        payload.progressiveUnitDiscounts = tenantForm.progressiveUnitDiscounts ?? [];
      }
      if (
        tenantForm.packageType === 'gestao' ||
        tenantForm.packageType === 'operador' ||
        tenantForm.packageType === 'institucional'
      ) {
        payload.contractedExtraUserPackageQuantity = null;
        payload.extraUserPricing = { individualPrice: 0, packages: [] };
      } else {
        payload.contractedExtraUserPackageQuantity =
          tenantForm.contractedExtraUserPackageQuantity ?? null;
      }
      payload.billingDueDay =
        tenantForm.billingDueDay != null &&
        tenantForm.billingDueDay >= 1 &&
        tenantForm.billingDueDay <= 31
          ? tenantForm.billingDueDay
          : null;
      if (tenantForm.packageType === 'institucional') {
        const packs = tenantForm.nucleusCapacityPacks ?? [];
        payload.nucleusCapacityPacks = packs;
        payload.contractedNucleusCapacity = tenantForm.contractedNucleusCapacity;
      }
      const response = await apiService.updateTenantPricingConfig(selectedTenantId, payload);
      if (response.data.success) {
        await loadConfig();
        handleCloseTenantForm();
        showSuccess('Configuração salva com sucesso!');
        loadTenants();
      }
    } catch (err: any) {
      showError(err.response?.data?.message || 'Erro ao salvar configuração do cliente');
    } finally {
      setTenantSaving(false);
    }
  };

  const handleStartCreatePricing = () => {
    setConfig({
      productId,
      isActive: true,
      availablePackages: [{ key: 'gestao', enabled: true, order: 1 }],
      packages: {
        gestao: {
          name: 'VOVER Planos',
          basePrice: 0,
          includedUsers: 3,
          extraUserPricing: { individualPrice: 15, packages: [] },
          condoCapacityPacks: [],
          maxReferralDiscountPercent: 0,
          progressiveUnitDiscounts: [],
          unifiedBillingForCondos: true
        }
      },
      tenantConfigs: []
    });
    setConfigMissing(false);
    setTabValue(0);
  };

  const reorderGestaoPacks = (fromIndex: number, toIndex: number) => {
    const packs = [...(config?.packages?.gestao?.condoCapacityPacks || [])];
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= packs.length ||
      toIndex >= packs.length ||
      fromIndex === toIndex
    ) {
      return;
    }
    const [moved] = packs.splice(fromIndex, 1);
    packs.splice(toIndex, 0, moved);
    updateGestao({
      condoCapacityPacks: packs.map((p: Record<string, unknown>, i: number) => ({
        ...p,
        order: i
      }))
    });
  };

  if (schemaEntry) {
    const { Component } = schemaEntry;
    return (
      <Suspense fallback={<CircularProgress />}>
        <Component productId={productId} />
      </Suspense>
    );
  }

  if (configMissing && !config) {
    return (
      <ProductScopeGate
        productId={productId}
        integration="core"
        loading={loading}
        loadError={loadError}
        isEmpty={false}
        onRetry={loadConfig}
      >
        <Box>
          <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    Configuração de Precificação
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Define planos, modelos de cobrança e exceções por cliente.
                  </Typography>
                  <ProductCell
                    productId={productId}
                    name={productsById.get(productId)?.displayName}
                    avatarUrl={productsById.get(productId)?.avatarUrl}
                  />
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={handleStartCreatePricing}>
                  Cadastrar precificação
                </Button>
              </Stack>
            </CardContent>
          </Card>
          <Alert severity="info">
            Não há configuração de precificação cadastrada para este produto. Os dados ainda não
            existem — use o botão acima para criar a primeira configuração.
          </Alert>
        </Box>
      </ProductScopeGate>
    );
  }

  if (!config) {
    return (
      <ProductScopeGate
        productId={productId}
        integration="core"
        loading={loading}
        loadError={loadError}
        isEmpty={false}
        onRetry={loadConfig}
      >
        <></>
      </ProductScopeGate>
    );
  }

  const pkgGestao = config.packages?.gestao;

  return (
    <ProductScopeGate
      productId={productId}
      integration="core"
      loading={loading}
      loadError={loadError}
      isEmpty={false}
      onRetry={loadConfig}
    >
      <Box>
        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h4">Configuração de Precificação</Typography>
              <Typography variant="body2" color="text.secondary">
                Define planos, modelos de cobrança e exceções por cliente. O MRR estimado no Fluxo
                financeiro usa esta configuração como base de projeção.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <ProductCell
                  productId={productId}
                  name={productsById.get(productId)?.displayName}
                  avatarUrl={productsById.get(productId)?.avatarUrl}
                />
                <Chip size="small" variant="outlined" label="Planos padrão" />
                <Chip size="small" variant="outlined" label="Overrides por cliente" />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label="VOVER Planos" />
          <Tab label="Por cliente" />
        </Tabs>

        {tabValue === 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              VOVER Planos — planos disponíveis para contratação (calculadora, signup e área do
              cliente)
            </Typography>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      (config?.availablePackages ?? []).find(
                        (ap: { key?: string }) => (ap.key || '').toLowerCase() === 'gestao'
                      )?.enabled !== false
                    }
                    onChange={e => updateGestaoEnabled(e.target.checked)}
                  />
                }
                label="Planos ativos (disponíveis na landing, signup e financeiro)"
              />
            </Box>

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Box>
                <Typography variant="h6">Planos</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Arraste pelo ícone ⋮⋮ para definir a ordem exibida na calculadora e no signup.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={() => {
                  const cur = [...(pkgGestao?.condoCapacityPacks || [])];
                  const newIndex = cur.length;
                  updateGestao({
                    condoCapacityPacks: [
                      ...cur,
                      { capacity: 1, price: 0, label: '', features: [], order: newIndex }
                    ]
                  });
                  setPendingPlanFocusIndex(newIndex);
                }}
              >
                Adicionar plano
              </Button>
            </Box>

            {(pkgGestao?.condoCapacityPacks || []).map(
              (cap: Record<string, unknown>, index: number) => (
                <Card
                  key={`plan-${index}-${String(cap.label ?? '')}`}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    opacity: dragPackIndex === index ? 0.55 : 1,
                    borderStyle: dragPackIndex === index ? 'dashed' : 'solid'
                  }}
                  draggable
                  onDragStart={() => setDragPackIndex(index)}
                  onDragEnd={() => setDragPackIndex(null)}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    if (dragPackIndex != null) reorderGestaoPacks(dragPackIndex, index);
                    setDragPackIndex(null);
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DragIndicator
                          sx={{ cursor: 'grab', color: 'text.secondary' }}
                          titleAccess="Arraste para reordenar"
                        />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Plano {index + 1}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          const cur = (pkgGestao?.condoCapacityPacks || []).filter(
                            (_: unknown, i: number) => i !== index
                          );
                          updateGestao({ condoCapacityPacks: cur });
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Nome do plano"
                          value={(cap.label as string) ?? ''}
                          inputRef={el => {
                            planLabelInputRefs.current[index] = el;
                          }}
                          onChange={e => {
                            const cur = [...(pkgGestao?.condoCapacityPacks || [])];
                            cur[index] = { ...cur[index], label: e.target.value };
                            updateGestao({ condoCapacityPacks: cur });
                          }}
                          placeholder="Nome do plano"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Ícone</InputLabel>
                          <Select
                            value={(cap.icon as string) ?? ''}
                            onChange={e => {
                              const cur = [...(pkgGestao?.condoCapacityPacks || [])];
                              const icon = e.target.value as string;
                              cur[index] = { ...cur[index] };
                              if (icon) cur[index].icon = icon;
                              else delete cur[index].icon;
                              updateGestao({ condoCapacityPacks: cur });
                            }}
                            label="Ícone"
                            displayEmpty
                            renderValue={v => {
                              if (!v) return 'Sem ícone';
                              const opt = PLAN_ICON_OPTIONS.find(o => o.value === v);
                              const IconComp = resolvePlanIcon(v);
                              if (opt && IconComp) {
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconComp fontSize="small" />
                                    {opt.label}
                                  </Box>
                                );
                              }
                              return v;
                            }}
                          >
                            <MenuItem value="">
                              <em>Sem ícone</em>
                            </MenuItem>
                            <ListSubheader>Escala de volume (menor → maior)</ListSubheader>
                            {PLAN_ICON_OPTIONS.filter(o => o.group === 'scale').map(
                              ({ value, label, Icon }) => (
                                <MenuItem key={value} value={value}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon fontSize="small" />
                                    {label}
                                  </Box>
                                </MenuItem>
                              )
                            )}
                            <ListSubheader>Outros (compatibilidade)</ListSubheader>
                            {PLAN_ICON_OPTIONS.filter(o => o.group === 'legacy').map(
                              ({ value, label, Icon }) => (
                                <MenuItem key={value} value={value}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon fontSize="small" />
                                    {label}
                                  </Box>
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={4} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Condomínios"
                          type="number"
                          value={cap.capacity ?? ''}
                          onChange={e => {
                            const cur = [...(pkgGestao?.condoCapacityPacks || [])];
                            cur[index] = {
                              ...cur[index],
                              capacity: parseInt(e.target.value, 10) || 0
                            };
                            updateGestao({ condoCapacityPacks: cur });
                          }}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={4} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Preço (R$)"
                          value={
                            rawInputValues[`gestao-pack-price-${index}`] !== undefined
                              ? rawInputValues[`gestao-pack-price-${index}`]
                              : formatCurrency((cap.price as number) ?? 0)
                          }
                          onChange={e =>
                            setRawInputValues(prev => ({
                              ...prev,
                              [`gestao-pack-price-${index}`]: e.target.value
                            }))
                          }
                          onFocus={() =>
                            setRawInputValues(prev => ({
                              ...prev,
                              [`gestao-pack-price-${index}`]: numberToEditableString(
                                (cap.price as number) ?? 0
                              )
                            }))
                          }
                          onBlur={() => {
                            const num = parseCurrency(
                              rawInputValues[`gestao-pack-price-${index}`] ?? ''
                            );
                            const cur = [...(pkgGestao?.condoCapacityPacks || [])];
                            cur[index] = { ...cur[index], price: num };
                            updateGestao({ condoCapacityPacks: cur });
                            setRawInputValues(prev => {
                              const next = { ...prev };
                              delete next[`gestao-pack-price-${index}`];
                              return next;
                            });
                          }}
                          inputProps={{ inputMode: 'decimal' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Destaque (opcional)"
                          value={(cap.highlight as string) ?? ''}
                          onChange={e => {
                            const cur = [...(pkgGestao?.condoCapacityPacks || [])];
                            cur[index] = { ...cur[index], highlight: e.target.value };
                            updateGestao({ condoCapacityPacks: cur });
                          }}
                          placeholder="Ex.: Melhor custo-benefício"
                        />
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                      Funcionalidades inclusas
                    </Typography>
                    {((cap.features as string[]) || []).map((feature: string, fIndex: number) => (
                      <Box key={fIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={feature}
                          onChange={e => {
                            const cur = [...(pkgGestao?.condoCapacityPacks || [])];
                            const features = [...((cur[index].features as string[]) || [])];
                            features[fIndex] = e.target.value;
                            cur[index] = { ...cur[index], features };
                            updateGestao({ condoCapacityPacks: cur });
                          }}
                          placeholder="Ex.: Gestão financeira completa"
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const cur = [...(pkgGestao?.condoCapacityPacks || [])];
                            const features = ((cur[index].features as string[]) || []).filter(
                              (_: string, i: number) => i !== fIndex
                            );
                            cur[index] = { ...cur[index], features };
                            updateGestao({ condoCapacityPacks: cur });
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={() => {
                        const cur = [...(pkgGestao?.condoCapacityPacks || [])];
                        const features = [...((cur[index].features as string[]) || []), ''];
                        cur[index] = { ...cur[index], features };
                        updateGestao({ condoCapacityPacks: cur });
                      }}
                    >
                      Adicionar funcionalidade
                    </Button>
                  </CardContent>
                </Card>
              )
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving} size="large">
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </Paper>
        )}

        {tabValue === 1 && (
          <Paper sx={{ p: 3 }}>
            {/* Filtros por cliente */}
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <AdvancedFilters
                config={{
                  showClientFilter: true,
                  showDateRangeFilter: false,
                  showSearchFilter: false,
                  pageType: 'pricing-client-config'
                }}
                selectedClients={selectedClients}
                onClientsChange={setSelectedClients}
              />

              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  loadTenants();
                  setTenantDialogOpen(true);
                }}
              >
                Nova Configuração por Cliente
              </Button>
            </Box>

            {/* Lista de clientes com precificação personalizada */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Clientes com precificação personalizada
              </Typography>

              {tenantsLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Carregando clientes...
                  </Typography>
                </Box>
              )}

              {config.tenantConfigs && config.tenantConfigs.length > 0 ? (
                <List>
                  {config.tenantConfigs
                    .filter(
                      (tc: any) =>
                        selectedClients.length === 0 || selectedClients.includes(tc.tenantId)
                    )
                    .map((tc: any) => {
                      const tenant = tenants.find(t => t.tenantId === tc.tenantId);
                      const displayName = tenant?.companyName || tc.tenantId;
                      return (
                        <ListItem
                          key={tc.tenantId}
                          secondaryAction={
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => handleOpenTenantForm(tc.tenantId, tc)}
                            >
                              Editar
                            </Button>
                          }
                        >
                          <ClientCell
                            name={displayName}
                            tenantId={tc.tenantId}
                            logoUrl={(tenant as any)?.logoUrl}
                            size="small"
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 2, flexShrink: 0 }}
                          >
                            {tc.packageType === 'institucional'
                              ? 'VOVER Institucional'
                              : tc.packageType === 'gestao' || tc.packageType === 'operador'
                                ? 'VOVER Gestão'
                                : 'VOVER Condomínio'}
                          </Typography>
                        </ListItem>
                      );
                    })}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhum cliente com precificação personalizada.
                </Typography>
              )}
            </Box>

            {/* Modal para escolher cliente ao criar nova configuração */}
            <Dialog
              open={tenantDialogOpen}
              onClose={() => setTenantDialogOpen(false)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Escolher cliente para configuração personalizada</DialogTitle>
              <DialogContent dividers>
                <TenantPickerList
                  tenants={tenants.map(t => ({
                    tenantId: String(t.tenantId),
                    ...(t.companyName ? { companyName: String(t.companyName) } : {}),
                    ...((t as { logoUrl?: string }).logoUrl
                      ? { logoUrl: String((t as { logoUrl?: string }).logoUrl) }
                      : {})
                  }))}
                  excludeTenantIds={(config.tenantConfigs || []).map(
                    (tc: { tenantId: string }) => tc.tenantId
                  )}
                  loading={tenantsLoading}
                  onSelect={tenantId => {
                    handleOpenTenantForm(tenantId);
                    setTenantDialogOpen(false);
                  }}
                  emptyMessage="Não há clientes disponíveis sem configuração personalizada."
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setTenantDialogOpen(false)}>Fechar</Button>
              </DialogActions>
            </Dialog>

            {/* Modal completo de configuração por cliente */}
            <Dialog
              open={tenantFormDialogOpen}
              onClose={handleCloseTenantForm}
              fullWidth
              maxWidth="lg"
              PaperProps={{ sx: { maxHeight: '90vh' } }}
            >
              <DialogTitle>
                Configuração personalizada para cliente:{' '}
                {selectedTenantId &&
                  (tenants.find(t => t.tenantId === selectedTenantId)?.companyName ||
                    selectedTenantId)}
              </DialogTitle>
              <DialogContent dividers sx={{ overflowY: 'auto' }}>
                {tenantValidationErrors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    As quantidades escolhidas são menores que o uso atual do cliente. Não é possível
                    salvar até ajustar:
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                      {tenantValidationErrors.map((msg, i) => (
                        <li key={i}>{msg}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                {tenantForm && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ mt: 1, mb: 2 }}>
                      Tipo de pacote
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Tipo de pacote</InputLabel>
                          <Select
                            value={
                              tenantForm.packageType === 'operador'
                                ? 'gestao'
                                : (tenantForm.packageType ??
                                  tenantFormPackageOptions[0]?.key ??
                                  'condominio')
                            }
                            onChange={e => handleTenantPackageTypeChange(e.target.value as string)}
                            label="Tipo de pacote"
                          >
                            {tenantFormPackageOptions.map((ap: any) => (
                              <MenuItem key={ap.key} value={ap.key}>
                                {ap.name ?? ap.key}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                      Configurações Básicas
                    </Typography>
                    <Grid container spacing={2}>
                      {tenantForm.packageType === 'condominio' && (
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Preço Mínimo por Cliente"
                            value={
                              tenantRawInputValues['tenant-minPricePerClient'] !== undefined
                                ? tenantRawInputValues['tenant-minPricePerClient']
                                : formatCurrency(tenantForm.minPricePerClient)
                            }
                            onChange={e =>
                              setTenantRawInputValues(prev => ({
                                ...prev,
                                'tenant-minPricePerClient': e.target.value
                              }))
                            }
                            onFocus={() => {
                              setTenantRawInputValues(prev => ({
                                ...prev,
                                'tenant-minPricePerClient': numberToEditableString(
                                  tenantForm.minPricePerClient
                                )
                              }));
                            }}
                            onBlur={() => {
                              const num = parseCurrency(
                                tenantRawInputValues['tenant-minPricePerClient'] ?? ''
                              );
                              setTenantForm((prev: any) => ({ ...prev, minPricePerClient: num }));
                              setTenantRawInputValues(prev => {
                                const next = { ...prev };
                                delete next['tenant-minPricePerClient'];
                                return next;
                              });
                            }}
                            margin="normal"
                            placeholder="R$ 0,00"
                            inputProps={{ inputMode: 'decimal' }}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Ciclo de Cobrança</InputLabel>
                          <Select
                            value={tenantForm.billingCycle || config.billingCycle || 'monthly'}
                            onChange={e =>
                              setTenantForm((prev: any) => ({
                                ...prev,
                                billingCycle: e.target.value
                              }))
                            }
                            label="Ciclo de Cobrança"
                          >
                            <MenuItem value="monthly">Mensal</MenuItem>
                            <MenuItem value="quarterly">Trimestral</MenuItem>
                            <MenuItem value="annual">Anual</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth margin="normal">
                          <InputLabel id="tenant-billing-due-day-label">
                            Dia de vencimento
                          </InputLabel>
                          <Select
                            labelId="tenant-billing-due-day-label"
                            label="Dia de vencimento"
                            value={tenantForm.billingDueDay ?? ''}
                            onChange={e => {
                              const v = e.target.value;
                              const num = v === '' ? undefined : parseInt(String(v), 10);
                              setTenantForm((prev: any) => ({
                                ...prev,
                                billingDueDay: Number.isFinite(num) ? num : undefined
                              }));
                            }}
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>Padrão (config global)</em>
                            </MenuItem>
                            {[5, 10, 15, 20, 25].map(d => (
                              <MenuItem key={d} value={d}>
                                {d}
                              </MenuItem>
                            ))}
                          </Select>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, display: 'block' }}
                          >
                            Dia do mês para vencimento da fatura (5, 10, 15, 20 ou 25)
                          </Typography>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {tenantForm.packageType === 'gestao' && (
                      <>
                        <Divider sx={{ my: 4 }} />
                        <Typography variant="h6" gutterBottom>
                          Desconto progressivo por volume total
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Exclusivo para clientes parceiros: desconto sobre o valor do pacote de
                          capacidade, conforme o total de unidades somadas nos condomínios. Vazio =
                          sem desconto neste cliente.
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            Faixas (total de unidades)
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={handleTenantAddProgressiveUnitDiscount}
                          >
                            Adicionar faixa
                          </Button>
                        </Box>
                        {(tenantForm.progressiveUnitDiscounts || []).map(
                          (tier: any, index: number) => (
                            <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                              <Grid item xs={12} sm={4} md={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Unidades mín."
                                  type="number"
                                  value={tier.minTotalUnits ?? ''}
                                  onChange={e =>
                                    handleTenantUpdateProgressiveUnitDiscount(
                                      index,
                                      'minTotalUnits',
                                      parseInt(e.target.value, 10) || 0
                                    )
                                  }
                                  inputProps={{ min: 0, step: 1 }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4} md={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Unidades máx. (opcional)"
                                  type="number"
                                  value={tier.maxTotalUnits ?? ''}
                                  onChange={e => {
                                    const v = e.target.value;
                                    handleTenantUpdateProgressiveUnitDiscount(
                                      index,
                                      'maxTotalUnits',
                                      v === '' ? null : parseInt(v, 10) || 0
                                    );
                                  }}
                                  placeholder="∞"
                                  inputProps={{ min: 0, step: 1 }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4} md={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Desconto (%)"
                                  type="number"
                                  value={tier.discountPercentage ?? ''}
                                  onChange={e =>
                                    handleTenantUpdateProgressiveUnitDiscount(
                                      index,
                                      'discountPercentage',
                                      Math.min(15, Math.max(0, parseInt(e.target.value, 10) || 0))
                                    )
                                  }
                                  inputProps={{ min: 0, max: 15, step: 1 }}
                                  helperText="Máximo 15%"
                                />
                              </Grid>
                              <Grid item xs={12} md={1}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleTenantDeleteProgressiveUnitDiscount(index)}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Grid>
                            </Grid>
                          )
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            mt: 3
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            Capacidade de condomínios (override)
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() =>
                              setTenantForm((prev: any) => ({
                                ...prev,
                                condoCapacityPacks: [
                                  ...(prev.condoCapacityPacks || []),
                                  { capacity: 10, price: 0 }
                                ]
                              }))
                            }
                          >
                            Adicionar
                          </Button>
                        </Box>
                        {(tenantForm.condoCapacityPacks || []).map((cap: any, index: number) => (
                          <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                            <Grid item xs={6} md={2}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Condomínios"
                                type="number"
                                value={cap.capacity ?? ''}
                                onChange={e => {
                                  const cur = [...(tenantForm.condoCapacityPacks || [])];
                                  cur[index] = {
                                    ...cur[index],
                                    capacity: parseInt(e.target.value, 10) || 0
                                  };
                                  setTenantForm((prev: any) => ({
                                    ...prev,
                                    condoCapacityPacks: cur
                                  }));
                                }}
                              />
                            </Grid>
                            <Grid item xs={6} md={2}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Preço (R$)"
                                value={
                                  tenantRawInputValues[`tenant-condoPack-price-${index}`] !==
                                  undefined
                                    ? tenantRawInputValues[`tenant-condoPack-price-${index}`]
                                    : formatCurrency(cap.price ?? 0)
                                }
                                onChange={e =>
                                  setTenantRawInputValues((prev: any) => ({
                                    ...prev,
                                    [`tenant-condoPack-price-${index}`]: e.target.value
                                  }))
                                }
                                onFocus={() =>
                                  setTenantRawInputValues((prev: any) => ({
                                    ...prev,
                                    [`tenant-condoPack-price-${index}`]: numberToEditableString(
                                      cap.price ?? 0
                                    )
                                  }))
                                }
                                onBlur={() => {
                                  const num = parseCurrency(
                                    tenantRawInputValues[`tenant-condoPack-price-${index}`] ?? ''
                                  );
                                  const cur = [...(tenantForm.condoCapacityPacks || [])];
                                  cur[index] = { ...cur[index], price: num };
                                  setTenantForm((prev: any) => ({
                                    ...prev,
                                    condoCapacityPacks: cur
                                  }));
                                  setTenantRawInputValues((prev: any) => {
                                    const next = { ...prev };
                                    delete next[`tenant-condoPack-price-${index}`];
                                    return next;
                                  });
                                }}
                                inputProps={{ inputMode: 'decimal' }}
                              />
                            </Grid>
                          </Grid>
                        ))}
                        {(tenantForm.condoCapacityPacks || []).length > 0 && (
                          <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>Capacidade em uso pelo cliente</InputLabel>
                                <Select
                                  value={
                                    tenantForm.contractedClientCapacity ??
                                    tenantForm.condoCapacityPacks?.[0]?.capacity ??
                                    ''
                                  }
                                  onChange={e =>
                                    setTenantForm((prev: any) => ({
                                      ...prev,
                                      contractedClientCapacity: Number(e.target.value)
                                    }))
                                  }
                                  label="Capacidade em uso pelo cliente"
                                >
                                  {(tenantForm.condoCapacityPacks || []).map(
                                    (cap: any, idx: number) => (
                                      <MenuItem key={idx} value={cap.capacity ?? 0}>
                                        {cap.capacity ?? 0} condomínios –{' '}
                                        {formatCurrency(cap.price ?? 0)}
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                                <Typography variant="caption" color="text.secondary">
                                  Qual capacidade o cliente escolheu e contratou
                                </Typography>
                              </FormControl>
                            </Grid>
                          </Grid>
                        )}

                        <Divider sx={{ my: 4 }} />
                        <Alert severity="info">
                          Novo modelo: <strong>usuários ilimitados</strong> (Gestão). Não existe
                          mais precificação por usuários extras.
                        </Alert>
                      </>
                    )}

                    {tenantForm.packageType === 'institucional' && (
                      <>
                        <Divider sx={{ my: 4 }} />
                        <Typography variant="h6" gutterBottom>
                          Institucional — Pacotes de núcleos (o que foi escolhido pelo cliente)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Lista de pacotes de núcleos configurados. Adicione ou edite conforme o
                          cliente escolheu.
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            mb: 2
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() =>
                              setTenantForm((prev: any) => ({
                                ...prev,
                                nucleusCapacityPacks: [
                                  ...(prev.nucleusCapacityPacks || []),
                                  { capacity: 3, price: 0 }
                                ]
                              }))
                            }
                          >
                            Adicionar
                          </Button>
                        </Box>
                        {(tenantForm.nucleusCapacityPacks || []).map((cap: any, index: number) => (
                          <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                            <Grid item xs={6} md={2}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Núcleos"
                                type="number"
                                value={cap.capacity ?? ''}
                                onChange={e => {
                                  const cur = [...(tenantForm.nucleusCapacityPacks || [])];
                                  cur[index] = {
                                    ...cur[index],
                                    capacity: parseInt(e.target.value, 10) || 0
                                  };
                                  setTenantForm((prev: any) => ({
                                    ...prev,
                                    nucleusCapacityPacks: cur
                                  }));
                                }}
                              />
                            </Grid>
                            <Grid item xs={6} md={2}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Preço (R$)"
                                value={
                                  tenantRawInputValues[`tenant-nucleusPack-price-${index}`] !==
                                  undefined
                                    ? tenantRawInputValues[`tenant-nucleusPack-price-${index}`]
                                    : formatCurrency(cap.price ?? 0)
                                }
                                onChange={e =>
                                  setTenantRawInputValues((prev: any) => ({
                                    ...prev,
                                    [`tenant-nucleusPack-price-${index}`]: e.target.value
                                  }))
                                }
                                onFocus={() =>
                                  setTenantRawInputValues((prev: any) => ({
                                    ...prev,
                                    [`tenant-nucleusPack-price-${index}`]: numberToEditableString(
                                      cap.price ?? 0
                                    )
                                  }))
                                }
                                onBlur={() => {
                                  const num = parseCurrency(
                                    tenantRawInputValues[`tenant-nucleusPack-price-${index}`] ?? ''
                                  );
                                  const cur = [...(tenantForm.nucleusCapacityPacks || [])];
                                  cur[index] = { ...cur[index], price: num };
                                  setTenantForm((prev: any) => ({
                                    ...prev,
                                    nucleusCapacityPacks: cur
                                  }));
                                  setTenantRawInputValues((prev: any) => {
                                    const next = { ...prev };
                                    delete next[`tenant-nucleusPack-price-${index}`];
                                    return next;
                                  });
                                }}
                                inputProps={{ inputMode: 'decimal' }}
                              />
                            </Grid>
                          </Grid>
                        ))}
                        {(tenantForm.nucleusCapacityPacks || []).length > 0 && (
                          <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>Pacote de núcleo contratado pelo cliente</InputLabel>
                                <Select
                                  value={
                                    tenantForm.contractedNucleusCapacity ??
                                    tenantForm.nucleusCapacityPacks?.[0]?.capacity ??
                                    ''
                                  }
                                  onChange={e =>
                                    setTenantForm((prev: any) => ({
                                      ...prev,
                                      contractedNucleusCapacity: Number(e.target.value)
                                    }))
                                  }
                                  label="Pacote de núcleo contratado pelo cliente"
                                >
                                  {(tenantForm.nucleusCapacityPacks || []).map(
                                    (cap: any, idx: number) => (
                                      <MenuItem key={idx} value={cap.capacity ?? 0}>
                                        {cap.capacity ?? 0} núcleos –{' '}
                                        {formatCurrency(cap.price ?? 0)}
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                                <Typography variant="caption" color="text.secondary">
                                  Qual pacote de núcleo o cliente escolheu e contratou
                                </Typography>
                              </FormControl>
                            </Grid>
                          </Grid>
                        )}

                        <Divider sx={{ my: 4 }} />
                        <Alert severity="info">
                          Novo modelo: <strong>usuários ilimitados</strong>. Não há precificação por
                          usuários inclusos ou extras neste pacote.
                        </Alert>
                      </>
                    )}

                    {tenantForm.packageType === 'condominio' && (
                      <>
                        <Divider sx={{ my: 4 }} />

                        {/* Price Tiers */}
                        <Box sx={{ mb: 4 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 2
                            }}
                          >
                            <Typography variant="h6">Faixas de Preço</Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Add />}
                              onClick={handleTenantAddPriceTier}
                            >
                              Adicionar Faixa
                            </Button>
                          </Box>

                          {(tenantForm.priceTiers || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Nenhuma faixa de preço configurada
                            </Typography>
                          ) : (
                            <>
                              <Grid container spacing={2}>
                                {(tenantForm.priceTiers || []).map((tier: any, index: number) => (
                                  <Grid item xs={12} key={index}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            mb: 2
                                          }}
                                        >
                                          <Typography variant="subtitle2" fontWeight="bold">
                                            Faixa {index + 1}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleTenantDeletePriceTier(index)}
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </Box>
                                        <Grid container spacing={2}>
                                          <Grid item xs={12} sm={4}>
                                            <TextField
                                              fullWidth
                                              label="Unidades Mínimas"
                                              type="number"
                                              value={
                                                tenantRawNumericValues[
                                                  `tenant-tier-minUnits-${index}`
                                                ] !== undefined
                                                  ? tenantRawNumericValues[
                                                      `tenant-tier-minUnits-${index}`
                                                    ]
                                                  : (tier.minUnits ?? '')
                                              }
                                              onChange={e =>
                                                setTenantRawNumericValues(prev => ({
                                                  ...prev,
                                                  [`tenant-tier-minUnits-${index}`]: e.target.value
                                                }))
                                              }
                                              onBlur={() => {
                                                const raw =
                                                  tenantRawNumericValues[
                                                    `tenant-tier-minUnits-${index}`
                                                  ];
                                                if (raw === undefined) return;
                                                const num = raw === '' ? 0 : parseInt(raw, 10);
                                                handleTenantUpdatePriceTier(
                                                  index,
                                                  'minUnits',
                                                  isNaN(num) ? 0 : num
                                                );
                                                setTenantRawNumericValues(prev => {
                                                  const next = { ...prev };
                                                  delete next[`tenant-tier-minUnits-${index}`];
                                                  return next;
                                                });
                                              }}
                                              size="small"
                                              inputProps={{ min: 0, step: 1 }}
                                            />
                                          </Grid>
                                          <Grid item xs={12} sm={4}>
                                            <TextField
                                              fullWidth
                                              label="Unidades Máximas (opcional)"
                                              type="number"
                                              value={
                                                tenantRawNumericValues[
                                                  `tenant-tier-maxUnits-${index}`
                                                ] !== undefined
                                                  ? tenantRawNumericValues[
                                                      `tenant-tier-maxUnits-${index}`
                                                    ]
                                                  : (tier.maxUnits ?? '')
                                              }
                                              onChange={e =>
                                                setTenantRawNumericValues(prev => ({
                                                  ...prev,
                                                  [`tenant-tier-maxUnits-${index}`]: e.target.value
                                                }))
                                              }
                                              onBlur={() => {
                                                const raw =
                                                  tenantRawNumericValues[
                                                    `tenant-tier-maxUnits-${index}`
                                                  ];
                                                if (raw === undefined) return;
                                                if (raw === '') {
                                                  handleTenantUpdatePriceTier(
                                                    index,
                                                    'maxUnits',
                                                    null
                                                  );
                                                } else {
                                                  const parsed = parseInt(raw, 10);
                                                  handleTenantUpdatePriceTier(
                                                    index,
                                                    'maxUnits',
                                                    isNaN(parsed) ? null : parsed
                                                  );
                                                }
                                                setTenantRawNumericValues(prev => {
                                                  const next = { ...prev };
                                                  delete next[`tenant-tier-maxUnits-${index}`];
                                                  return next;
                                                });
                                              }}
                                              size="small"
                                              helperText="Deixe vazio para ilimitado"
                                              inputProps={{ min: 0, step: 1 }}
                                            />
                                          </Grid>
                                          <Grid item xs={12} sm={4}>
                                            <TextField
                                              fullWidth
                                              label="Preço por Unidade"
                                              value={
                                                tenantRawInputValues[
                                                  `tenant-tier-pricePerUnit-${index}`
                                                ] !== undefined
                                                  ? tenantRawInputValues[
                                                      `tenant-tier-pricePerUnit-${index}`
                                                    ]
                                                  : formatCurrency(tier.pricePerUnit)
                                              }
                                              onChange={e =>
                                                setTenantRawInputValues(prev => ({
                                                  ...prev,
                                                  [`tenant-tier-pricePerUnit-${index}`]:
                                                    e.target.value
                                                }))
                                              }
                                              onFocus={() => {
                                                setTenantRawInputValues(prev => ({
                                                  ...prev,
                                                  [`tenant-tier-pricePerUnit-${index}`]:
                                                    numberToEditableString(tier.pricePerUnit)
                                                }));
                                              }}
                                              onBlur={() => {
                                                const num = parseCurrency(
                                                  tenantRawInputValues[
                                                    `tenant-tier-pricePerUnit-${index}`
                                                  ] ?? ''
                                                );
                                                handleTenantUpdatePriceTier(
                                                  index,
                                                  'pricePerUnit',
                                                  num
                                                );
                                                setTenantRawInputValues(prev => {
                                                  const next = { ...prev };
                                                  delete next[`tenant-tier-pricePerUnit-${index}`];
                                                  return next;
                                                });
                                              }}
                                              size="small"
                                              placeholder="R$ 0,00"
                                              inputProps={{ inputMode: 'decimal' }}
                                            />
                                          </Grid>
                                        </Grid>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </>
                          )}
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        {/* Volume Discounts */}
                        <Box sx={{ mb: 4 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 2
                            }}
                          >
                            <Typography variant="h6">Descontos por Volume</Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Add />}
                              onClick={handleTenantAddVolumeDiscount}
                            >
                              Adicionar Desconto
                            </Button>
                          </Box>

                          {(tenantForm.volumeDiscounts || []).length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Nenhum desconto por volume configurado
                            </Typography>
                          ) : (
                            <Grid container spacing={2}>
                              {(tenantForm.volumeDiscounts || []).map(
                                (discount: any, index: number) => (
                                  <Grid item xs={12} key={index}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            mb: 2
                                          }}
                                        >
                                          <Typography variant="subtitle2" fontWeight="bold">
                                            Desconto {index + 1}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleTenantDeleteVolumeDiscount(index)}
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </Box>
                                        <Grid container spacing={2}>
                                          <Grid item xs={12} sm={6}>
                                            <TextField
                                              fullWidth
                                              label="Unidades Totais Mínimas"
                                              type="number"
                                              value={
                                                tenantRawNumericValues[
                                                  `tenant-discount-minTotalUnits-${index}`
                                                ] !== undefined
                                                  ? tenantRawNumericValues[
                                                      `tenant-discount-minTotalUnits-${index}`
                                                    ]
                                                  : (discount.minTotalUnits ?? '')
                                              }
                                              onChange={e =>
                                                setTenantRawNumericValues(prev => ({
                                                  ...prev,
                                                  [`tenant-discount-minTotalUnits-${index}`]:
                                                    e.target.value
                                                }))
                                              }
                                              onBlur={() => {
                                                const raw =
                                                  tenantRawNumericValues[
                                                    `tenant-discount-minTotalUnits-${index}`
                                                  ];
                                                if (raw === undefined) return;
                                                const num = raw === '' ? 0 : parseInt(raw, 10);
                                                handleTenantUpdateVolumeDiscount(
                                                  index,
                                                  'minTotalUnits',
                                                  isNaN(num) ? 0 : num
                                                );
                                                setTenantRawNumericValues(prev => {
                                                  const next = { ...prev };
                                                  delete next[
                                                    `tenant-discount-minTotalUnits-${index}`
                                                  ];
                                                  return next;
                                                });
                                              }}
                                              size="small"
                                              inputProps={{ min: 0, step: 1 }}
                                            />
                                          </Grid>
                                          <Grid item xs={12} sm={6}>
                                            <TextField
                                              fullWidth
                                              label="Percentual de Desconto"
                                              value={
                                                tenantRawInputValues[
                                                  `tenant-discount-discountPercentage-${index}`
                                                ] !== undefined
                                                  ? tenantRawInputValues[
                                                      `tenant-discount-discountPercentage-${index}`
                                                    ]
                                                  : formatPercentage(discount.discountPercentage)
                                              }
                                              onChange={e =>
                                                setTenantRawInputValues(prev => ({
                                                  ...prev,
                                                  [`tenant-discount-discountPercentage-${index}`]:
                                                    e.target.value
                                                }))
                                              }
                                              onFocus={() => {
                                                setTenantRawInputValues(prev => ({
                                                  ...prev,
                                                  [`tenant-discount-discountPercentage-${index}`]:
                                                    numberToEditableString(
                                                      discount.discountPercentage
                                                    )
                                                }));
                                              }}
                                              onBlur={() => {
                                                const num =
                                                  parsePercentage(
                                                    tenantRawInputValues[
                                                      `tenant-discount-discountPercentage-${index}`
                                                    ] ?? ''
                                                  ) ?? 0;
                                                handleTenantUpdateVolumeDiscount(
                                                  index,
                                                  'discountPercentage',
                                                  num
                                                );
                                                setTenantRawInputValues(prev => {
                                                  const next = { ...prev };
                                                  delete next[
                                                    `tenant-discount-discountPercentage-${index}`
                                                  ];
                                                  return next;
                                                });
                                              }}
                                              size="small"
                                              placeholder="0,00%"
                                              inputProps={{ inputMode: 'decimal' }}
                                            />
                                          </Grid>
                                        </Grid>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )
                              )}
                            </Grid>
                          )}
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        {/* Extra User Pricing */}
                        <Box sx={{ mb: 4 }}>
                          <Typography variant="h6" gutterBottom>
                            Precificação de Usuários Extras
                          </Typography>

                          <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Preço Individual por Usuário Extra"
                                value={
                                  tenantRawInputValues[
                                    'tenant-extraUserPricing-individualPrice'
                                  ] !== undefined
                                    ? tenantRawInputValues[
                                        'tenant-extraUserPricing-individualPrice'
                                      ]
                                    : formatCurrency(tenantForm.extraUserPricing?.individualPrice)
                                }
                                onChange={e =>
                                  setTenantRawInputValues(prev => ({
                                    ...prev,
                                    'tenant-extraUserPricing-individualPrice': e.target.value
                                  }))
                                }
                                onFocus={() => {
                                  setTenantRawInputValues(prev => ({
                                    ...prev,
                                    'tenant-extraUserPricing-individualPrice':
                                      numberToEditableString(
                                        tenantForm.extraUserPricing?.individualPrice
                                      )
                                  }));
                                }}
                                onBlur={() => {
                                  const num = parseCurrency(
                                    tenantRawInputValues[
                                      'tenant-extraUserPricing-individualPrice'
                                    ] ?? ''
                                  );
                                  setTenantForm((prev: any) => ({
                                    ...prev,
                                    extraUserPricing: {
                                      ...(prev.extraUserPricing || { packages: [] }),
                                      individualPrice: num
                                    }
                                  }));
                                  setTenantRawInputValues(prev => {
                                    const next = { ...prev };
                                    delete next['tenant-extraUserPricing-individualPrice'];
                                    return next;
                                  });
                                }}
                                margin="normal"
                                placeholder="R$ 0,00"
                                inputProps={{ inputMode: 'decimal' }}
                              />
                            </Grid>
                          </Grid>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 2
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight="bold">
                              Pacotes de Usuários Extras
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Add />}
                              onClick={handleTenantAddExtraUserPackage}
                            >
                              Adicionar Pacote
                            </Button>
                          </Box>

                          {!tenantForm.extraUserPricing?.packages ||
                          tenantForm.extraUserPricing.packages.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Nenhum pacote de usuários extras configurado
                            </Typography>
                          ) : (
                            <Grid container spacing={2}>
                              {(tenantForm.extraUserPricing.packages || []).map(
                                (pkg: any, index: number) => (
                                  <Grid item xs={12} key={index}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            mb: 2
                                          }}
                                        >
                                          <Typography variant="subtitle2" fontWeight="bold">
                                            Pacote {index + 1}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                              handleTenantDeleteExtraUserPackage(index)
                                            }
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </Box>
                                        <Grid container spacing={2}>
                                          <Grid item xs={12} sm={6}>
                                            <TextField
                                              fullWidth
                                              label="Quantidade de Usuários"
                                              type="number"
                                              value={
                                                tenantRawNumericValues[
                                                  `tenant-package-quantity-${index}`
                                                ] !== undefined
                                                  ? tenantRawNumericValues[
                                                      `tenant-package-quantity-${index}`
                                                    ]
                                                  : (pkg.quantity ?? '')
                                              }
                                              onChange={e =>
                                                setTenantRawNumericValues(prev => ({
                                                  ...prev,
                                                  [`tenant-package-quantity-${index}`]:
                                                    e.target.value
                                                }))
                                              }
                                              onBlur={() => {
                                                const raw =
                                                  tenantRawNumericValues[
                                                    `tenant-package-quantity-${index}`
                                                  ];
                                                if (raw === undefined) return;
                                                const num = raw === '' ? 1 : parseInt(raw, 10);
                                                handleTenantUpdateExtraUserPackage(
                                                  index,
                                                  'quantity',
                                                  isNaN(num) ? 1 : num
                                                );
                                                setTenantRawNumericValues(prev => {
                                                  const next = { ...prev };
                                                  delete next[`tenant-package-quantity-${index}`];
                                                  return next;
                                                });
                                              }}
                                              size="small"
                                              inputProps={{ min: 1, step: 1 }}
                                            />
                                          </Grid>
                                          <Grid item xs={12} sm={6}>
                                            <TextField
                                              fullWidth
                                              label="Preço do Pacote"
                                              value={
                                                tenantRawInputValues[
                                                  `tenant-package-price-${index}`
                                                ] !== undefined
                                                  ? tenantRawInputValues[
                                                      `tenant-package-price-${index}`
                                                    ]
                                                  : formatCurrency(pkg.price)
                                              }
                                              onChange={e =>
                                                setTenantRawInputValues(prev => ({
                                                  ...prev,
                                                  [`tenant-package-price-${index}`]: e.target.value
                                                }))
                                              }
                                              onFocus={() => {
                                                setTenantRawInputValues(prev => ({
                                                  ...prev,
                                                  [`tenant-package-price-${index}`]:
                                                    numberToEditableString(pkg.price)
                                                }));
                                              }}
                                              onBlur={() => {
                                                const num = parseCurrency(
                                                  tenantRawInputValues[
                                                    `tenant-package-price-${index}`
                                                  ] ?? ''
                                                );
                                                handleTenantUpdateExtraUserPackage(
                                                  index,
                                                  'price',
                                                  num
                                                );
                                                setTenantRawInputValues(prev => {
                                                  const next = { ...prev };
                                                  delete next[`tenant-package-price-${index}`];
                                                  return next;
                                                });
                                              }}
                                              size="small"
                                              placeholder="R$ 0,00"
                                              inputProps={{ inputMode: 'decimal' }}
                                            />
                                          </Grid>
                                        </Grid>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                )
                              )}
                            </Grid>
                          )}
                          <Grid container spacing={2} sx={{ mt: 3 }}>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>
                                  Pacote de usuários extras em uso pelo cliente
                                </InputLabel>
                                <Select
                                  value={
                                    tenantForm.contractedExtraUserPackageQuantity != null &&
                                    tenantForm.contractedExtraUserPackageQuantity > 0
                                      ? tenantForm.contractedExtraUserPackageQuantity
                                      : 0
                                  }
                                  onChange={e => {
                                    const v = e.target.value;
                                    const num = typeof v === 'number' ? v : Number(v);
                                    setTenantForm((prev: any) => ({
                                      ...prev,
                                      contractedExtraUserPackageQuantity: num === 0 ? 0 : num
                                    }));
                                  }}
                                  label="Pacote de usuários extras em uso pelo cliente"
                                >
                                  <MenuItem value={0}>Individual (por usuário)</MenuItem>
                                  {(tenantForm.extraUserPricing?.packages || [])
                                    .filter((p: any) => (p.quantity ?? 0) > 0)
                                    .map((pkg: any, idx: number) => (
                                      <MenuItem key={idx} value={pkg.quantity ?? 0}>
                                        {pkg.quantity ?? 0} usuários –{' '}
                                        {formatCurrency(pkg.price ?? 0)}
                                      </MenuItem>
                                    ))}
                                </Select>
                                <Typography variant="caption" color="text.secondary">
                                  Qual pacote de usuários extras o cliente contratou (além dos
                                  incluídos)
                                </Typography>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleCloseTenantForm} variant="outlined">
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveTenantConfig}
                  variant="contained"
                  disabled={tenantSaving || tenantValidationErrors.length > 0}
                >
                  {tenantSaving ? 'Salvando...' : 'Salvar Configuração'}
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
        )}
      </Box>
    </ProductScopeGate>
  );
};

export default PricingPage;
